import React, { useEffect, useRef, useState } from 'react';
import {
  MessageSquare,
  Phone,
  Calendar,
  ClipboardList,
  Crown,
  Shield,
  User as UserIcon,
  Mail,
  X,
  Activity,
} from 'lucide-react';
import { SpaceMember } from '../../context/TenantContext.tsx';
import { User } from '../../../shared/types.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';

export type ProfileMember = SpaceMember | (User & Partial<SpaceMember>);

export interface MemberProfilePopoverProps {
  member: ProfileMember;
  teamName?: string;
  onClose: () => void;
  onScheduleOneOnOne?: (member: ProfileMember) => void;
  onViewAssignedTasks?: (member: ProfileMember) => void;
}

export const MemberProfilePopover: React.FC<MemberProfilePopoverProps> = ({
  member,
  teamName,
  onClose,
  onScheduleOneOnOne,
  onViewAssignedTasks,
}) => {
  const { user, token } = useAuth();
  const { onlineUserIds } = useRealtime();
  const popoverRef = useRef<HTMLDivElement>(null);

  const [liveCounts, setLiveCounts] = useState<{
    active?: number;
    inReview?: number;
    overdue?: number;
  }>({
    active: typeof (member as any).activeTasksCount === 'number' ? (member as any).activeTasksCount : undefined,
    inReview: typeof (member as any).inReviewTasksCount === 'number' ? (member as any).inReviewTasksCount : undefined,
    overdue: typeof (member as any).overdueTasksCount === 'number' ? (member as any).overdueTasksCount : undefined,
  });

  useEffect(() => {
    if (typeof (member as any).activeTasksCount === 'number') {
      setLiveCounts({
        active: (member as any).activeTasksCount,
        inReview: (member as any).inReviewTasksCount,
        overdue: (member as any).overdueTasksCount,
      });
      return;
    }

    let isMounted = true;
    const fetchMemberTasks = async () => {
      try {
        const authToken =
          token ||
          localStorage.getItem('pulse_auth_token') ||
          sessionStorage.getItem('pulse_auth_token') ||
          localStorage.getItem('token');
        const res = await fetch(`/api/tasks?assigneeId=${member.id}`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        if (res.ok && isMounted) {
          const data = await res.json();
          const tasks = data.tasks || [];
          const now = new Date();
          const active = tasks.filter(
            (t: any) => t.status !== 'done' && t.status !== 'completed' && !t.isArchived
          ).length;
          const inReview = tasks.filter((t: any) => t.status === 'in_review').length;
          const overdue = tasks.filter((t: any) => {
            if (t.status === 'done' || t.status === 'completed' || t.isArchived) return false;
            if (!t.dueDate) return false;
            return new Date(t.dueDate) < now;
          }).length;
          setLiveCounts({ active, inReview, overdue });
        }
      } catch (err) {
        // Safe silent fallback
      }
    };
    fetchMemberTasks();
    return () => {
      isMounted = false;
    };
  }, [member.id, token]);

  const isViewerOrGuest = user?.role === 'viewer' || user?.role === 'guest';
  const isOnline =
    (onlineUserIds instanceof Set
      ? onlineUserIds.has(member.id)
      : Array.isArray(onlineUserIds) && (onlineUserIds as any).includes(member.id)) ||
    member.id === user?.id;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const firstName = (member.fullName || 'Member').split(' ')[0];
  const username =
    (member as any).username ||
    (member.email ? member.email.split('@')[0] : firstName.toLowerCase()).replace(
      /[^a-z0-9._-]/g,
      ''
    );

  const isLead =
    (member as any).spaceRole === 'lead' ||
    (member as any).role === 'team_lead' ||
    (member as any).role === 'lead';

  const roleLabel =
    (member as any).systemRole === 'admin' || (member as any).role === 'admin'
      ? 'Admin'
      : isLead
      ? 'Team Lead'
      : (member as any).role === 'manager'
      ? 'Manager'
      : (member as any).role === 'guest'
      ? 'Guest'
      : (member as any).role === 'viewer'
      ? 'Viewer'
      : 'Member';

  const activeCount = liveCounts.active ?? (member as any).activeTasksCount ?? 0;
  const inReviewCount = liveCounts.inReview ?? (member as any).inReviewTasksCount ?? 0;
  const overdueCount = liveCounts.overdue ?? (member as any).overdueTasksCount ?? 0;
  const workloadBadge =
    (member as any).workloadStatus ||
    (activeCount >= 5 ? 'High Workload' : isOnline ? 'Available' : 'Away');

  const handleMessageMember = () => {
    window.dispatchEvent(
      new CustomEvent('open-floating-chat', {
        detail: {
          userId: member.id,
          userName: member.fullName,
          userAvatar: member.avatarUrl || undefined,
          userRole: member.title || roleLabel,
        },
      })
    );
    onClose();
  };

  const handleCallMember = () => {
    window.dispatchEvent(
      new CustomEvent('start-call', {
        detail: {
          targetUserId: member.id,
          targetUserName: member.fullName,
          targetUserAvatar: member.avatarUrl || undefined,
          targetUserRole: member.title || roleLabel,
          callType: 'audio',
        },
      })
    );
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        ref={popoverRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 rounded-3xl shadow-modal overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Top Accent Banner */}
        <div className="h-16 bg-gradient-to-r from-[#0162fc] via-[#2350fa] to-[#4b2efa] relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2.5 right-2.5 p-1.5 rounded-lg bg-black/25 hover:bg-black/40 text-white transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Profile Identity */}
        <div className="px-5 pb-5 -mt-8 relative">
          <div className="flex items-end justify-between gap-2 mb-3">
            <div className="relative">
              <div
                className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0162fc] to-[#4b2efa] text-white flex items-center justify-center text-xl font-bold shadow-md overflow-hidden ${
                  isLead
                    ? 'ring-3 ring-amber-400'
                    : 'ring-3 ring-white dark:ring-surface-900'
                }`}
              >
                {member.avatarUrl ? (
                  <img
                    src={member.avatarUrl}
                    alt={member.fullName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  (member.fullName || 'U').charAt(0).toUpperCase()
                )}
              </div>
              {/* Real-time online presence dot */}
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white dark:border-surface-900 ${
                  isOnline ? 'bg-emerald-500' : 'bg-surface-400'
                }`}
                title={isOnline ? 'Online' : 'Offline'}
              />
              {isLead && (
                <span
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center shadow"
                  title="Team Lead"
                >
                  <Crown className="w-3 h-3" />
                </span>
              )}
            </div>

            {/* Presence & Workload Badges */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  isOnline
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-surface-200/60 dark:bg-surface-800 text-surface-500'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    isOnline ? 'bg-emerald-500' : 'bg-surface-400'
                  }`}
                />
                {isOnline ? 'Online' : 'Offline'}
              </span>

              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  workloadBadge === 'High Workload'
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                    : workloadBadge === 'Available'
                    ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
                    : 'bg-surface-200/60 dark:bg-surface-800 text-surface-500'
                }`}
              >
                <Activity className="w-2.5 h-2.5" />
                {workloadBadge}
              </span>
            </div>
          </div>

          {/* Name, @username, Email, Role Badge */}
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-extrabold text-surface-900 dark:text-surface-100 leading-tight">
                {member.fullName}
              </h3>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                  roleLabel === 'Admin'
                    ? 'bg-purple-500/10 text-purple-600 dark:text-purple-300'
                    : roleLabel === 'Team Lead'
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-300'
                    : roleLabel === 'Manager'
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
                    : 'bg-blue-500/10 text-blue-600 dark:text-blue-300'
                }`}
              >
                {roleLabel === 'Admin' ? (
                  <Shield className="w-2.5 h-2.5" />
                ) : roleLabel === 'Team Lead' ? (
                  <Crown className="w-2.5 h-2.5" />
                ) : (
                  <UserIcon className="w-2.5 h-2.5" />
                )}
                {roleLabel}
              </span>
            </div>

            <p className="text-xs font-mono text-brand-600 dark:text-brand-400 font-semibold truncate">
              @{username}
              {member.title
                ? ` · ${member.title}`
                : (member as any).departmentName
                ? ` · ${(member as any).departmentName}`
                : ''}
            </p>

            {member.email && (
              <p className="text-xs text-surface-500 dark:text-surface-400 flex items-center gap-1.5 pt-0.5">
                <Mail className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{member.email}</span>
              </p>
            )}
          </div>

          {/* Live Team Workload Summary */}
          <div className="mt-4 p-3 rounded-xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200/60 dark:border-surface-800 flex items-center justify-around text-center">
            <div>
              <span className="text-sm font-extrabold text-surface-900 dark:text-surface-100">
                {activeCount}
              </span>
              <span className="block text-[10px] font-semibold text-surface-500 uppercase tracking-wider">
                Active Tasks
              </span>
            </div>
            <div className="h-6 w-px bg-surface-200 dark:bg-surface-700" />
            <div>
              <span className="text-sm font-extrabold text-amber-500">
                {inReviewCount}
              </span>
              <span className="block text-[10px] font-semibold text-surface-500 uppercase tracking-wider">
                In Review
              </span>
            </div>
            <div className="h-6 w-px bg-surface-200 dark:bg-surface-700" />
            <div>
              <span
                className={`text-sm font-extrabold ${
                  overdueCount > 0 ? 'text-rose-500' : 'text-surface-400'
                }`}
              >
                {overdueCount}
              </span>
              <span className="block text-[10px] font-semibold text-surface-500 uppercase tracking-wider">
                Overdue
              </span>
            </div>
          </div>

          {/* Quick Action Buttons (Hidden for Viewer and Guest) */}
          {!isViewerOrGuest && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleMessageMember}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold shadow-2xs transition-all cursor-pointer"
              >
                <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">Message {firstName}</span>
              </button>

              <button
                type="button"
                onClick={handleCallMember}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-2xs transition-all cursor-pointer"
              >
                <Phone className="w-3.5 h-3.5 shrink-0" />
                <span>Call</span>
              </button>

              {onScheduleOneOnOne ? (
                <button
                  type="button"
                  onClick={() => {
                    onScheduleOneOnOne(member);
                    onClose();
                  }}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-surface-50 hover:bg-surface-100 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-xs font-bold transition-all cursor-pointer"
                >
                  <Calendar className="w-3.5 h-3.5 text-brand-500 shrink-0" />
                  <span>Schedule 1-on-1</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(
                      new CustomEvent('open-schedule-meeting', {
                        detail: {
                          member,
                          targetUserId: member.id,
                          targetUserName: member.fullName,
                        },
                      })
                    );
                    onClose();
                  }}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-surface-50 hover:bg-surface-100 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-xs font-bold transition-all cursor-pointer"
                >
                  <Calendar className="w-3.5 h-3.5 text-brand-500 shrink-0" />
                  <span>Schedule 1-on-1</span>
                </button>
              )}

              {onViewAssignedTasks && (
                <button
                  type="button"
                  onClick={() => {
                    onViewAssignedTasks(member);
                    onClose();
                  }}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-surface-50 hover:bg-surface-100 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-700 text-xs font-bold transition-all cursor-pointer"
                >
                  <ClipboardList className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <span>Assigned Tasks</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberProfilePopover;
